"use client";

import type { IResource } from "@gettactic/api";
import { useIsFetching } from "@tanstack/react-query";
import React, { useEffect, useRef } from "react";
import { ResourceSettingsPanel } from "@/features/reserve/components/ResourceSettingsPanel/ResourceSettingsPanel";
import { useEditResourceRouter } from "@/features/reserve/hooks/useEditResourceRouter";
import {
  ResourceSettingsActionType,
  ResourceSettingsProvider
} from "@/features/reserve/hooks/useResourceSettings";

/** What a floor plan draws, and so what a click on it may open. */
const EDITABLE_TYPES: IResource["type"][] = [
  "desk",
  "workspace",
  "meeting_room",
  "parking_space",
  "parking_lot"
];

/**
 * The caches the panel invalidates after saving anything about a resource:
 * its fields, who it is dedicated to, which teams may book it. A refresh
 * one of them was told to make while the panel is open is the sign that
 * something changed.
 */
const RESOURCE_CACHES = new Set([
  "organizations.resourcesAll",
  "organizations.resourceScheduledPermissionsAll",
  "organizations.getResourceAdditionalAccess",
  "resources.dedicatedUsers",
  "resource"
]);

interface DeskEditorProps {
  officeId: string;
  /** The resource to open; the editor closes when it is done with it. */
  resourceId: string;
  onClose: () => void;
  /**
   * Something about the resource changed; the answers around the map are
   * stale. Must not refresh the resource caches itself, or it fires again.
   */
  onSaved: () => void;
}

const DeskEditorBody: React.FC<DeskEditorProps> = ({
  officeId,
  resourceId,
  onClose,
  onSaved
}) => {
  const {
    resourceContext,
    isSideDrawerOpen,
    setIsSideDrawerOpen,
    resourceList
  } = useEditResourceRouter(officeId, EDITABLE_TYPES);
  // The panel opens once the office's resources are in, and is handed the
  // resource directly: the hook's own opener rewrites the address, which
  // remounts the analytics page around the map. Opened once per resource,
  // so a refetch after saving does not reopen a panel just closed.
  const resource = resourceList.find((r) => r.id === resourceId);
  const { dispatch } = resourceContext;
  const openedFor = useRef<string | null>(null);
  useEffect(() => {
    if (resource && openedFor.current !== resourceId) {
      openedFor.current = resourceId;
      dispatch({
        type: ResourceSettingsActionType.SetSelectedResource,
        payload: resource
      });
      setIsSideDrawerOpen(true);
    }
  }, [resource, resourceId, dispatch, setIsSideDrawerOpen]);

  // The panel's tabs save on their own and invalidate the resource caches
  // afterwards; not every one of them reports back. A refresh those caches
  // were told to make is the sign of a change once it settles; the panel
  // loading its resource, or the map refreshing behind it, is not.
  const fetching = useIsFetching({
    predicate: (query) =>
      RESOURCE_CACHES.has(String(query.queryKey[0])) &&
      query.state.isInvalidated
  });
  const wasFetching = useRef(false);
  useEffect(() => {
    if (fetching > 0) {
      wasFetching.current = true;
      return;
    }
    if (wasFetching.current) {
      wasFetching.current = false;
      onSaved();
    }
  }, [fetching, onSaved]);

  return (
    <ResourceSettingsPanel
      isOpen={isSideDrawerOpen}
      onOpenChange={(isOpen) => {
        setIsSideDrawerOpen(isOpen);
        if (!isOpen) {
          onClose();
        }
      }}
      onEdit={(hasError) => {
        if (!hasError) {
          onSaved();
        }
      }}
    />
  );
};

/**
 * The office settings' resource editor, opened over an analytics map for
 * one desk or room: who it is dedicated to, which teams may book it, its
 * hours. The same panel the settings pages use.
 */
export const DeskEditor: React.FC<DeskEditorProps> = (props) => (
  <ResourceSettingsProvider>
    <DeskEditorBody {...props} />
  </ResourceSettingsProvider>
);
